import { Badge } from "../ui/badge";
import { ClockCounterClockwise, Bank, Warning } from "@phosphor-icons/react";
import { fmtDateTime } from "../../lib/dateUtil";

const BANK_FIELDS = new Set(["bank_account_name", "bank_sort_code", "bank_account_number"]);
const LABELS = {
  first_name: "First name", last_name: "Last name", position: "Position", email: "Contact email", contact_number: "Contact number",
  street_address: "Street address", address_line_2: "Address line 2", city: "Town / City", county: "County", post_code: "Postcode",
  company_name: "Company name", company_registration_number: "Company reg. no.", vat_number: "VAT number", company_email: "Company email",
  company_phone: "Company phone", company_website: "Website", uses_subcontractors: "Uses subcontractors",
  subcontractor_percentage: "% subcontracted", subcontractors_vetted: "Subcontractors vetted", emergency_contact_methods: "Emergency contact methods",
  core_trade: "Services", areas_covered: "Areas covered", specialist_skills: "Specialist skills", trade_qualifications: "Trade qualifications",
  specialist_certifications: "Certifications", call_out_charges: "Call-out charges", payment_terms: "Payment terms", legal_documents_held: "Legal documents held",
  bank_account_name: "Bank account name", bank_sort_code: "Sort code", bank_account_number: "Account number",
};
const label = f => LABELS[f] || f.replace(/_/g, " ");

function HistoryEntry({ e }) {
  const emailChanged = e.previous_email && e.new_email && e.previous_email.toLowerCase() !== e.new_email.toLowerCase();
  return (
    <div className={`rounded-lg border p-3 ${e.bank_changed ? "border-red-300 bg-red-50/60 dark:bg-red-950/20 dark:border-red-900/50" : "border-border bg-muted/20"}`}
      data-testid={`update-history-entry-${e.id}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 text-xs">
          <span className="font-semibold">{fmtDateTime(e.at)}</span>
          <Badge variant="outline" className="text-[10px]">{e.source}</Badge>
          {e.external_reference && <span className="text-muted-foreground font-mono text-[10px]">{e.external_reference}</span>}
        </div>
        {e.bank_changed && (
          <Badge className="bg-red-600 text-white text-[10px] gap-1" data-testid={`update-history-bank-flag-${e.id}`}>
            <Bank size={11} weight="fill" /> Bank details changed
          </Badge>
        )}
      </div>
      <div className="flex flex-wrap gap-1 mt-2">
        {(e.changed_fields || []).length === 0 && <span className="text-[11px] text-muted-foreground">No field values changed</span>}
        {(e.changed_fields || []).map(f => (
          <span key={f} className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${BANK_FIELDS.has(f) ? "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300" : "bg-secondary text-secondary-foreground"}`}>
            {label(f)}
          </span>
        ))}
        {e.documents_added > 0 && <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300">+{e.documents_added} document{e.documents_added !== 1 ? "s" : ""}</span>}
      </div>
      {emailChanged && <p className="text-[11px] text-muted-foreground mt-1.5">Email {e.previous_email} → <span className="font-medium text-foreground">{e.new_email}</span></p>}
      {e.bank_changed && (
        <p className="text-[11px] text-red-700 dark:text-red-300 mt-1.5 flex items-center gap-1"><Warning size={12} weight="fill" /> Verify the new bank details with the supplier by phone before paying any invoice.</p>
      )}
    </div>
  );
}

export default function SupplierUpdateHistoryCard({ history }) {
  const items = [...(history || [])].reverse();
  const bankCount = items.filter(e => e.bank_changed).length;
  return (
    <div className="border border-border rounded-lg p-5 bg-card" data-testid="supplier-update-history-card">
      <div className="flex items-center gap-2 mb-4">
        <ClockCounterClockwise size={18} className="text-muted-foreground" weight="duotone" />
        <h2 className="text-base font-semibold font-[Chivo]">Change History</h2>
        <span className="text-xs text-muted-foreground">({items.length} self-service update{items.length !== 1 ? "s" : ""})</span>
        {bankCount > 0 && <Badge className="bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300 text-[10px] ml-auto">{bankCount} bank change{bankCount !== 1 ? "s" : ""}</Badge>}
      </div>
      {items.length === 0 ? (
        <p className="text-xs text-muted-foreground" data-testid="update-history-empty">No self-service updates yet. Use <span className="font-semibold">Update link</span> to let the supplier refresh their own details.</p>
      ) : (
        <div className="space-y-2">{items.map(e => <HistoryEntry key={e.id || e.at} e={e} />)}</div>
      )}
    </div>
  );
}
